/* eslint-disable id-length */
import { Outlet, useMatches, useLocation } from "react-router-dom";
import { useCallback, useEffect, useRef, useLayoutEffect } from "react";
import PausedClusterQueuesNotice from "~/components/shared/PausedClusterQueuesNotice";
import BuildContentNavigation from "~/components/build/Show/components/BuildContentNavigation";
import { BuildView } from "~/components/build/Show/lib/types/BuildView";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import Header from "~/components/build/Header";
import Sidebar from "~/components/build/Show/components/Sidebar";
import { PipelineInfo } from "~/components/build/Header/PipelineInfo";
import { Branch } from "~/components/build/Header/Branch";
import { Commit } from "~/components/build/Header/Commit";
import BuildDuration from "~/components/build/Duration";
import PipelineStateIcon, {
  VARIANTS,
} from "~/components/shared/PipelineStateIcon/PipelineStateIcon";
import { twMerge } from "tailwind-merge";
import { getBuildColor } from "~/components/build/Show/lib/getBuildColor";
import { CollapsedBuildActions } from "~/components/build/Show/components/CollapsedBuildActions";
import BuildNumber from "~/components/build/Header/BuildNumber";
import { PullRequest } from "~/components/build/Header/PullRequest";
import NewBuildUIToggleDialog from "~/components/build/Show/components/NewBuildUIToggleDialog";
import {
  clampSidebarWidth,
  useBuildPreferencesStore,
} from "~/components/build/Show/lib/useBuildPreferencesStore";
import JobLogLineStore from "~/stores/JobLogLineStore";
import JobLogGroupStore from "~/stores/JobLogGroupStore";
import { parseHash } from "~/stores/BuildPageStore";
import { ResizeHandle } from "~/components/build/Show/components/ResizeHandle";
import classNames from "classnames";
import StepDrawer from "~/components/build/Show/components/StepDrawer";
import useQueryParams from "~/components/build/Show/lib/useQueryParams";
import { Revel } from "~/components/build/Show/components/Revel";
import debounce from "lodash/debounce";
import Icon from "~/components/shared/Icon";
import { AnimatePresence, LayoutGroup, motion } from "framer-motion";
import {
  FilterByOption,
  useFilterStore,
  useStateCounts,
} from "~/components/build/Show/components/Sidebar/useFilterStore";
import { BuildPagePreferences } from "~/components/build/Show/components/BuildPagePreferences";
import {
  useAdjustDockPositionForContainer,
  DockPosition,
} from "~/components/build/Show/lib/useAdjustedDockPosition";
import { useTrack } from "~/components/build/Show/lib/useTrack";
import { useFocusOnFailure } from "~/components/build/Show/components/Sidebar/GoToFailure";
import { useHotkeys, useHotkeysContext } from "react-hotkeys-hook";
import { isBuildFailing, isBuildFinished } from "~/components/build/Show/lib/types/Build";
import { useSteps } from "~/components/build/Show/lib/useSteps";

const SIDEBAR_TRANSITION = {
  type: "spring",
  duration: 0.2,
};

/**
 * The "classic" build layout, which is used when the new build UI is not enabled.
 *
 * @deprecated
 */
function ClassicBuildLayout() {
  const { build, store } = useBuild();
  const matches = useMatches();
  const view = [...matches].pop()?.id as BuildView;

  return (
    <>
      <PausedClusterQueuesNotice pausedQueues={build.dispatchPausedClusterQueues} />
      <Header
        build={build}
        showRebuild={true}
        store={store}
        currentView={view}
        showJobs={view === BuildView.JobList}
      />
      <div className="mb-4 flex justify-between border-b border-gray-400 px-3 pr-0">
        <BuildContentNavigation />
        <NewBuildUIToggleDialog optIn={true} />
      </div>

      <Outlet />
    </>
  );
}

/**
 * Build layout (aka the "BBUI")
 */
function BuildLayout() {
  const { build } = useBuild();

  const { stepUuid, jobUuid, open } = useQueryParams();
  const stepOrJobId = stepUuid || jobUuid;

  const theme = getBuildColor(build);

  const widthRef = useRef(useBuildPreferencesStore().sidebarWidth);
  const setWidth = useBuildPreferencesStore((state) => state.setSidebarWidth);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);
  useAdjustDockPositionForContainer("#drawer-container");
  const dockPosition = useBuildPreferencesStore((state) => state.dockPosition);
  const focusOnFailure = useFocusOnFailure();
  const counts = useStateCounts();
  const { enableScope, disableScope } = useHotkeysContext();

  /**
   * Update the hotkey scope when the drawer is active
   */
  useEffect(() => {
    if (stepOrJobId && open !== "false") {
      enableScope("drawer");
    } else {
      disableScope("drawer");
    }
  }, [stepOrJobId, open, enableScope, disableScope]);

  useHotkeys("f", focusOnFailure);

  const debounceSyncWidth = useCallback(
    debounce((width: number) => setWidth(width), 250),
    [],
  );

  const setFilterBy = useFilterStore((state) => state.setFilterBy);
  const { steps } = useSteps();

  /**
   * Auto-apply "Failed" filter if the build has executed and finished with failures, or if it's started failing.
   */
  useEffect(() => {
    if (Features.ProgressiveBuildShow && build.applyFailedFilter) {
      setFilterBy([FilterByOption.Failed]);
      return;
    }

    if ((isBuildFinished(build) || isBuildFailing(build)) && counts[FilterByOption.Failed] > 0) {
      setFilterBy([FilterByOption.Failed]);
    }
  }, [build.applyFailedFilter, build.state, steps.length]);

  const resizeSidebar = useCallback(
    (x: number) => {
      if (!sidebarRef.current || !handleRef.current) {
        return;
      }

      const offset =
        handleRef.current.getBoundingClientRect().left -
        sidebarRef.current.getBoundingClientRect().width;

      const width = clampSidebarWidth(x - offset);
      sidebarRef.current.style.width = `${width}px`;
      debounceSyncWidth(width);
      widthRef.current = width;
    },
    [setWidth],
  );

  const isSidebarOpen = useBuildPreferencesStore((state) =>
    Features.HideBuildSidebar ? false : state.isSidebarOpen,
  );
  const toggleSidebar = useBuildPreferencesStore((state) => state.toggleSidebar);

  useHotkeys("meta+b", toggleSidebar, { enabled: !Features.HideBuildSidebar });

  return (
    <div className="relative -mt-2.5 flex min-w-0 flex-auto flex-col gap-2">
      <div data-testid="bbui-build-header" className="flex flex-wrap gap-x-2 gap-y-2">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-2 sm:flex-nowrap">
          {Features.HideBuildSidebar ? null : <ToggleSidebarButton />}
          <PipelineInfo />

          <div className="flex min-w-0 flex-initial shrink-[2] items-center text-sm">
            <span className="hidden shrink-0 select-none px-1 text-gray-500 sm:!block">/</span>

            <Branch {...build} className="min-w-8 max-w-40 px-2" />

            {build.commitId && (
              <>
                <span className="shrink-0 select-none px-1 text-gray-500">/</span>

                <Commit {...build} className="min-w-8 max-w-40 px-2" />

                {build.pullRequest && (
                  <span className="shrink-0 pr-2">
                    (<PullRequest {...build} />)
                  </span>
                )}
              </>
            )}

            <span className="shrink-0 select-none px-1 text-gray-500">/</span>

            <div className="flex shrink-0 items-center gap-1 px-2 sm:mr-2">
              <BuildNumber {...build} />
            </div>
          </div>
        </div>

        <div className="flex flex-auto items-center justify-between gap-4">
          <div className="inline-flex min-w-0 flex-initial items-center gap-2 text-sm">
            <div className="flex shrink-0 items-center gap-1.5">
              <PipelineStateIcon
                build={build}
                style={{
                  width: "20px",
                  height: "20px",
                  color: theme.primaryColor,
                }}
                variant={VARIANTS.large}
              />
              <BuildDuration build={build} />
            </div>
          </div>

          <div className="flex min-w-0 gap-1">
            <CollapsedBuildActions />
          </div>
        </div>
      </div>

      <PausedClusterQueuesNotice pausedQueues={build.dispatchPausedClusterQueues} />

      <div className="flex min-w-0 flex-auto flex-row">
        <LayoutGroup>
          <AnimatePresence initial={false}>
            {isSidebarOpen && (
              <motion.div
                initial={{ opacity: 0, marginLeft: `-${widthRef.current}px` }}
                animate={{ opacity: 1, marginLeft: 0 }}
                exit={{ opacity: 0, marginLeft: `-${widthRef.current}px` }}
                transition={SIDEBAR_TRANSITION}
                className="hidden min-w-0 flex-col gap-4 md:!flex"
                style={{ width: `${widthRef.current}px` }}
                ref={sidebarRef}
              >
                <div className="relative mr-3 h-full">
                  <Sidebar />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <motion.div
            layout={true}
            id="drawer-container"
            transition={SIDEBAR_TRANSITION}
            className={classNames(
              "@container/main relative flex min-w-0 flex-auto flex-row-reverse gap-2 md:min-w-[540px]",
              {
                "flex-col-reverse": dockPosition === DockPosition.Bottom,
              },
            )}
          >
            {isSidebarOpen && (
              <ResizeHandle
                direction="horizontal"
                onResize={resizeSidebar}
                ref={handleRef}
                className="left-[1px] rounded-l-md"
              />
            )}
            {/* As an optimization, render the step drawer **before** the main outlet to handle cases with excessive DOM nodes */}
            {stepOrJobId && <StepDrawer key={stepOrJobId} stepOrJobId={stepOrJobId} />}

            <div className="@container/outlet relative flex min-w-0 flex-auto flex-col rounded-md md:min-w-[540px]">
              <div
                className={classNames(
                  "flex min-w-0 flex-auto flex-col rounded-md border border-gray-500 bg-slate-100",
                )}
              >
                <div
                  className={twMerge(
                    "flex border-b border-gray-400 px-2 shadow-sm",
                    classNames({
                      "pr-2": Features.BuildSidebarToggle,
                    }),
                  )}
                >
                  <BuildContentNavigation />

                  <div className="ml-auto flex items-center gap-2">
                    {!Features.HideUIToggle && <NewBuildUIToggleDialog optIn={false} />}
                    <BuildPagePreferences />
                  </div>
                </div>

                <Outlet />
              </div>
            </div>
          </motion.div>
        </LayoutGroup>
      </div>
    </div>
  );
}

const ToggleSidebarButton = () => {
  const track = useTrack();
  const isSidebarOpen = useBuildPreferencesStore((state) => state.isSidebarOpen);
  const toggleSidebar = useBuildPreferencesStore((state) => state.toggleSidebar);

  const handleClick = useCallback(() => {
    toggleSidebar();
    track("Build Sidebar Toggled", { state: isSidebarOpen ? "collapsed" : "expanded" });
  }, [toggleSidebar, isSidebarOpen]);

  let userAgentMetaIcon = "⌘";
  if (typeof window !== "undefined") {
    userAgentMetaIcon = navigator.userAgent.includes("Mac") ? "⌘" : "Ctrl";
  }

  const hotKey = `${userAgentMetaIcon}+B`;

  return (
    <button
      data-testid="sidebar-toggle"
      onClick={handleClick}
      title={isSidebarOpen ? `Collapse sidebar (${hotKey})` : `Expand sidebar (${hotKey})`}
      className="hidden h-6 w-6 items-center justify-center rounded-sm bg-transparent transition-colors hover:bg-purple-100 hover:text-purple-600 md:!flex"
    >
      <Icon
        icon="custom/outline/sidebar-left"
        className={classNames("h-[18px] w-[18px]", {
          "[&>path:nth-child(2)]:fill-current [&>path:nth-child(n+3)]:stroke-white": isSidebarOpen,
        })}
      />
    </button>
  );
};

export default () => {
  // If the URL references a log line we'll mark it as highlighted and expand its log
  // group in the stores straight away
  // Downstream components can decide how to control scrolling to the line
  const { hash } = useLocation();
  // If a hash contains a group then we're referencing a log line (no need to check the line too)
  const { job, group } = parseHash(hash) || {};
  const lineId = group ? hash.slice(1) : null;
  useLayoutEffect(() => {
    if (Features.RefactorLogScrollBehaviour && lineId) {
      JobLogLineStore.highlight(lineId);
      JobLogGroupStore.set(`${job}/${group}`, true);
    }
  }, [job, group, lineId]);

  return (
    <>
      {Features.BuildSidebar ? <BuildLayout /> : <ClassicBuildLayout />}
      <Revel />
    </>
  );
};
